import * as React from "react"
import {
  DimensionSortMode,
  Interval,
  type LayoutComponentType,
  type Statement,
  type StatementConfig,
  useReadReportStatementLiveDataQuery,
  type ViewIdentifierInput,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import dayjs from "dayjs"
import usePreviousDistinct from "src/shared/hooks/usePreviousDistinct"

/**
 * Must be used within a LayoutContext.
 */
export function useStatementLiveData(
  baseConfig: StatementConfig,
  type: LayoutComponentType,
  viewId: ViewIdentifierInput,
  { noLaterThanToday = false, skip }: { noLaterThanToday?: boolean; skip?: boolean } = {}
) {
  const config = React.useMemo(() => {
    if (!noLaterThanToday) {
      return baseConfig
    }
    const end = dateTimeHelper.endOfIntervalOrigin(baseConfig.origin)
    // Do not query for future dates
    if (end.isAfter(dayjs().startOf("day").utc())) {
      return {
        ...baseConfig,
        origin: dateTimeHelper.intervalOriginFromDayjs(dayjs().startOf("day").utc(), Interval.Day),
      }
    }
    return baseConfig
  }, [baseConfig, noLaterThanToday])

  const { data, loading, error } = useReadReportStatementLiveDataQuery({
    variables: {
      viewId,
      config: {
        type,
        statement: config,
      },
      limit: 5,
      sort: DimensionSortMode.Total,
      timeHistory:
        !config.options?.categoryByDepartmentOptions && !config.options?.categoryByLocationOptions,
      departmentHistory: Boolean(config.options?.categoryByDepartmentOptions),
      locationHistory: Boolean(config.options?.categoryByLocationOptions),
    },
    skip,
    // Disable caching to prevent memory leaks from accumulating statement data
    fetchPolicy: "no-cache",
  })
  const previousData = usePreviousDistinct(data)

  return React.useMemo(() => {
    const liveData = data?.liveComponentData ?? previousData?.liveComponentData

    // Resolves the component data union types to the portions we care about
    if (liveData?.__typename === "Statement") {
      return { statement: liveData as Statement, loading, error }
    }

    return { statement: undefined, loading, error }
  }, [data?.liveComponentData, error, loading, previousData?.liveComponentData])
}
